import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { AdminShell } from "../../components/AdminShell";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  ResponsiveContainer,
  Tooltip,
  CartesianGrid,
  Cell,
} from "recharts";

export default function AdminReports() {
  const [r, setR] = useState(null);
  useEffect(() => { api.get("/reports/summary").then((res) => setR(res.data)); }, []);

  if (!r) return <AdminShell title="Reports"><div className="text-slate-400">Loading…</div></AdminShell>;

  const inventoryData = [
    { name: "Available", value: r.vehicles_available, color: "#10B981" },
    { name: "Pending", value: r.vehicles_pending, color: "#F59E0B" },
    { name: "Sold", value: r.vehicles_sold, color: "#E63946" },
  ];
  const pipelineData = [
    { name: "Total Leads", value: r.leads_total, color: "#0A2A66" },
    { name: "Open Leads", value: r.leads_open, color: "#D4AF37" },
    { name: "Deals", value: r.deals_total, color: "#3B82F6" },
    { name: "Signed", value: r.deals_signed, color: "#10B981" },
  ];

  return (
    <AdminShell title="Reports">
      {/* Real-time revenue strip — reflects only closed sales (signed/completed/sold) */}
      <div className="surface p-6 rounded-sm mb-6" data-testid="revenue-strip">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div data-testid="revenue-today-cell">
            <div className="label-eyebrow">Today</div>
            <div className="font-heading text-2xl mt-1" style={{ color: "var(--wakzy-gold)" }}>
              ${(r.revenue_today || 0).toLocaleString()}
            </div>
          </div>
          <div data-testid="revenue-mtd-cell">
            <div className="label-eyebrow">Month-to-date</div>
            <div className="font-heading text-2xl mt-1">
              ${(r.revenue_mtd || 0).toLocaleString()}
            </div>
          </div>
          <div data-testid="revenue-ytd-cell">
            <div className="label-eyebrow">Year-to-date</div>
            <div className="font-heading text-2xl mt-1">
              ${(r.revenue_ytd || 0).toLocaleString()}
            </div>
          </div>
          <div data-testid="revenue-avg-cell">
            <div className="label-eyebrow">Avg deal</div>
            <div className="font-heading text-2xl mt-1">
              ${(r.avg_deal_value || 0).toLocaleString()}
            </div>
          </div>
          <div data-testid="revenue-closed-cell">
            <div className="label-eyebrow">Closed deals</div>
            <div className="font-heading text-2xl mt-1">{r.closed_deal_count || 0}</div>
          </div>
        </div>
        <div className="text-[10px] text-slate-500 mt-3">
          Revenue includes only <span className="text-white">signed / completed / sold</span> deals. Drafts are excluded.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="surface p-6 rounded-sm">
          <div className="label-eyebrow mb-4">Inventory status</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={inventoryData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
              <YAxis stroke="#94A3B8" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B1324", border: "1px solid rgba(255,255,255,0.1)" }} />
              <Bar dataKey="value">
                {inventoryData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="surface p-6 rounded-sm">
          <div className="label-eyebrow mb-4">Sales pipeline</div>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={pipelineData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
              <YAxis stroke="#94A3B8" fontSize={12} />
              <Tooltip contentStyle={{ background: "#0B1324", border: "1px solid rgba(255,255,255,0.1)" }} />
              <Bar dataKey="value">
                {pipelineData.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="surface p-6 rounded-sm lg:col-span-2 grid grid-cols-3 gap-6">
          <div>
            <div className="label-eyebrow">Gross revenue (all closed)</div>
            <div className="font-heading text-4xl mt-2" style={{ color: "var(--wakzy-gold)" }} data-testid="gross-revenue">
              ${(r.gross_revenue || 0).toLocaleString()}
            </div>
          </div>
          <div>
            <div className="label-eyebrow">Finance penetration</div>
            <div className="font-heading text-4xl mt-2">{r.finance_penetration}%</div>
          </div>
          <div>
            <div className="label-eyebrow">Service appointments</div>
            <div className="font-heading text-4xl mt-2">{r.service_appointments}</div>
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
